import { MouseEvent, useCallback, useMemo } from 'react'

import { useTrackProductInteraction } from '../../../../../../app/_providers/TrackingProvider.client'
import { QuickBuyContainer } from '../../../../../../brunswick/containers/QuickBuyContainer'
import { SizeChartModalService } from '../../../../../../brunswick/containers/SizeChartModalService'
import { useSelectVariant } from '../../../../../../lib/hooks/useSelectVariant'
import { useProductHref } from '../../../../../../lib/routing'
import { deriveAvailableProducts } from '../../../../../../lib/utils'

import type { Props, ViewModel } from './types'

export function useViewModel({ isPlain, product }: Props): ViewModel {
  const { openSizeChartModal } = SizeChartModalService.useContainer()
  const { openModal: openQuickBuyModal } = QuickBuyContainer.useContainer()
  const { trackProductInteraction } = useTrackProductInteraction()

  // NOTE: The variants pulled from `product.variants` currently do not include extended size variants (FIGS-4449).
  // A subsequent GET_FEATURED_PRODUCT query is called that populates the variants correctly.
  const coloredAndFitVariants = product.variants ?? []

  const availableProducts = useMemo(() => {
    return deriveAvailableProducts(product.productGroup.productsForAvailability)
  }, [product.productGroup.productsForAvailability])

  const selectVariantOutput = useSelectVariant({
    availableProducts,
    coloredAndFitVariants,
    imageSize: 350,
    initialFeaturedProduct: product,
    initialSelectedColor: product.colorInfo,
    productGroup: product.productGroup,
  })

  const {
    productGroup: { handle: productGroupHandle },
  } = product

  const pdpHref = useProductHref(productGroupHandle, {
    color: selectVariantOutput.selectedColor?.rawName,
    plain: isPlain || undefined,
  })

  const onProductView = () => {
    trackProductInteraction({
      handle: product.handle || '',
      shopifyId: product.shopifyId,
      id: product.id,
    })
  }

  const revealSizeChartModal = useCallback(() => {
    product.productGroup.sizeChart && openSizeChartModal(product.productGroup.sizeChart)
  }, [product, openSizeChartModal])

  const onQuickBuyButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      if (!product.colorInfo || !coloredAndFitVariants) return
      openQuickBuyModal({
        analyticsContext: { category: 'Shop the Look Tile' },
        availableProducts,
        initialFeaturedProduct: product,
        initialSelectedColor: product.colorInfo,
        productGroup: product.productGroup,
        sizeChart: product.productGroup.sizeChart,
        variants: coloredAndFitVariants,
      })
    },
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      coloredAndFitVariants,
      openQuickBuyModal,
      product.colorInfo,
      product.productGroup,
      product.productGroup.sizeChart,
    ]
  )

  return {
    ...selectVariantOutput,
    onQuickBuyButtonClick,
    pdpHref,
    product,
    revealSizeChartModal,
    onProductView,
  }
}
